<template>
  <div class="statistics-page">
    <div class="toolbar">
      <h1 class="title">Статистика: ТрансМенеджер</h1>
      <span class="total-rows">Данные из выгрузки ТрансМенеджера</span>
      <div class="toolbar-actions">
        <button class="btn" type="button" :disabled="refreshing" @click="refresh">
          {{ refreshing ? 'Обновление…' : 'Обновить' }}
        </button>
      </div>
    </div>

    <div class="filters-panel">
      <button class="btn btn-reset" type="button" @click="resetFilters">Сбросить</button>
      <FilterPresetsDropdown storage-key="statisticsTransManagerFilterPresets" :current="filtersSnapshot" @apply="applyFiltersSnapshot" />
      <DatePresetsDropdown
        :date-from="filters.dateFrom"
        :date-to="filters.dateTo"
        :preset-id="filters.presetId"
        @change="onPeriodChange"
      />
      <MultiDropdown v-for="filter in FILTERS" :key="filter.id" class="filter-item" :label="filter.label" :options="options[filter.id] || []" :model-value="filters[filter.id]" @update:model-value="filters[filter.id] = $event" />
    </div>

    <div class="tabs">
      <button v-for="tab in TABS" :key="tab.id" class="tab-btn" :class="{ active: activeTab === tab.id }" type="button" @click="activeTab = tab.id">
        {{ tab.name }}
      </button>
    </div>

    <div class="tab-content">
      <TransManagerReportsTab v-show="activeTab === 'reports'" ref="reports" :filters="filters" @refreshing="setRefreshing" />
      <TransManagerConstructorTab v-show="activeTab === 'constructor'" ref="constructor" v-model:filters="filters" @refreshing="setRefreshing" />
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue';
import TransManagerConstructorTab from './components/TransManagerConstructorTab.vue';
import TransManagerReportsTab from './components/TransManagerReportsTab.vue';
import DatePresetsDropdown from './components/DatePresetsDropdown.vue';
import FilterPresetsDropdown from './components/FilterPresetsDropdown.vue';
import MultiDropdown from './components/MultiDropdown.vue';
import { fetchTransManagerFilterOptions } from '@/backend/transManagerApi';
import { presetRange } from './components/DatePresetsDropdown.vue';

const TABS = [
  { id: 'reports', name: 'Отчёты' },
  { id: 'constructor', name: 'Конструктор' },
];
const FILTERS = [
  { id: 'branch', label: 'Филиал' },
  { id: 'manager', label: 'Менеджер' },
  { id: 'customer', label: 'Заказчик' },
  { id: 'carrier', label: 'Перевозчик' },
  { id: 'status', label: 'Статус' },
  { id: 'direction', label: 'Направление' },
  { id: 'cityOut', label: 'Город отправки' },
  { id: 'cityIn', label: 'Город прибытия' },
];
const emptyFilters = () => ({
  dateFrom: '', dateTo: '', presetId: '', branch: [], manager: [], customer: [], carrier: [], status: [], direction: [], cityOut: [], cityIn: [],
});
const FILTER_STORAGE_KEY = 'statisticsTransManagerFilters';
const FILTER_KEYS = FILTERS.map((filter) => filter.id);
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function loadStoredFilters() {
  try {
    if (typeof window === 'undefined') return {};
    const value = JSON.parse(window.localStorage.getItem(FILTER_STORAGE_KEY));
    return value && typeof value === 'object' ? value : {};
  } catch (error) {
    return {};
  }
}

function restoreFilters() {
  const stored = loadStoredFilters();
  const filters = emptyFilters();
  const range = presetRange(stored.presetId);
  if (stored.presetId === 'custom') {
    filters.presetId = 'custom';
    filters.dateFrom = DATE_RE.test(stored.dateFrom) ? stored.dateFrom : '';
    filters.dateTo = DATE_RE.test(stored.dateTo) ? stored.dateTo : '';
  } else if (range) {
    filters.presetId = stored.presetId;
    filters.dateFrom = range.dateFrom;
    filters.dateTo = range.dateTo;
  } else {
    filters.dateFrom = DATE_RE.test(stored.dateFrom) ? stored.dateFrom : '';
    filters.dateTo = DATE_RE.test(stored.dateTo) ? stored.dateTo : '';
  }
  FILTER_KEYS.forEach((key) => {
    if (Array.isArray(stored[key])) filters[key] = stored[key].filter((value) => typeof value === 'string');
  });
  return filters;
}

export default {
  name: 'TransManagerStatisticsPage',
  components: { DatePresetsDropdown, FilterPresetsDropdown, MultiDropdown, TransManagerConstructorTab, TransManagerReportsTab },
  setup() {
    const activeTab = ref('reports');
    const filters = reactive(restoreFilters());
    const options = ref({});
    const refreshing = ref(false);
    const constructor = ref(null);
    const reports = ref(null);
    const setRefreshing = (value) => { refreshing.value = value; };
    const loadOptions = async () => { options.value = await fetchTransManagerFilterOptions(); };
    const refresh = async () => {
      await loadOptions();
      await Promise.all([constructor.value?.refresh?.(), reports.value?.refresh?.()]);
    };
    const resetFilters = () => {
      Object.assign(filters, emptyFilters());
      constructor.value?.reset?.();
    };
    const applyFiltersSnapshot = (snapshot) => Object.assign(filters, emptyFilters(), snapshot || {});
    const onPeriodChange = ({ dateFrom, dateTo, presetId }) => {
      filters.dateFrom = dateFrom;
      filters.dateTo = dateTo;
      filters.presetId = presetId || '';
    };
    const filtersSnapshot = computed(() => JSON.parse(JSON.stringify(filters)));
    watch(filters, (value) => {
      try {
        window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(value));
      } catch (error) {
        return undefined;
      }
      return undefined;
    }, { deep: true });
    loadOptions();
    return { TABS, FILTERS, activeTab, filters, options, filtersSnapshot, refreshing, constructor, reports, setRefreshing, refresh, resetFilters, applyFiltersSnapshot, onPeriodChange };
  },
};
</script>

<style scoped>
.statistics-page { display: flex; flex-direction: column; gap: 16px; height: 100%; padding: 20px 24px 40px; box-sizing: border-box; overflow-y: auto; }
.toolbar { display: flex; align-items: center; gap: 16px; }
.title { margin: 0; font-size: 22px; font-weight: 600; color: #111827; }
.total-rows { color: #667085; font-size: 13px; }
.toolbar-actions { margin-left: auto; display: flex; gap: 8px; }
.btn { padding: 8px 14px; font-size: 13px; border: 1px solid #d0d5dd; border-radius: 6px; background: #fff; color: #1d2939; cursor: pointer; }
.btn:hover:not(:disabled) { background: #f8fafc; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.btn-reset { border-color: #f04438; color: #d92d20; }
.btn-reset:hover:not(:disabled) { background: #fff3f2; }
.filters-panel { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 12px 14px; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; }
.filter-item { min-width: 0; }
.tabs { display: flex; flex-wrap: wrap; gap: 4px; border-bottom: 1px solid #e5e7eb; }
.tab-btn { padding: 10px 16px; font-size: 14px; border: 0; border-bottom: 2px solid transparent; background: none; color: #667085; cursor: pointer; }
.tab-btn.active { border-bottom-color: #3b82f6; color: #101828; font-weight: 600; }
.tab-content { flex: 1; min-height: 300px; }

@media screen and (max-width: 769px) {
  .toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
